import Image from 'next/image'
import { FunctionComponent } from 'react'
import { useMount } from 'react-use'
import styled from 'styled-components'
import type { OurIntlShape } from '@/i18n/typing'
import PlatformImage from '@/utils/export-pptx/components/platform-image'
import {
  Card,
  CardTitle,
  SlideContainer,
  SlideTitle,
} from '@/utils/export-pptx/components/slides/styles'
import { PptxObjectType } from '@/utils/export-pptx/constants'
import type { CommentSentimentRankingSlide } from '@/utils/export-pptx/types/slide'
import { getThumbnail } from '@/utils/get-thumbnails'

interface SlideProps {
  slide: CommentSentimentRankingSlide
  onSlideReady?: (slide: CommentSentimentRankingSlide) => void
  formatMessage: OurIntlShape['formatMessage']
}

const SlideComponent: FunctionComponent<SlideProps> = ({
  slide,
  onSlideReady,
  formatMessage,
}) => {
  useMount(() => {
    onSlideReady?.(slide)
  })

  return (
    <SlideContainer data-pptx-object={PptxObjectType.Shape}>
      <SlideTitle data-pptx-object={PptxObjectType.Shape} mb={12}>
        <PlatformImage
          height={54}
          platform={slide.payload.platform}
          width={54}
        />

        <span data-pptx-object={PptxObjectType.Text}>
          {formatMessage({ id: 'autoreport:analysis_section_comment' })}
        </span>
      </SlideTitle>

      <ContentGrid>
        {slide.payload.positive && (
          <Card data-pptx-object={PptxObjectType.Shape}>
            <CardTitle data-pptx-object={PptxObjectType.Text} mb={5}>
              {slide.payload.positive.title}
            </CardTitle>

            <CardItem>
              <CardItemHeader data-pptx-object={PptxObjectType.Text}>
                {formatMessage({ id: 'autoreport:chart_field_order' })}
              </CardItemHeader>
              <CardItemHeader data-pptx-object={PptxObjectType.Text}>
                KOL
              </CardItemHeader>
              <CardItemHeader data-pptx-object={PptxObjectType.Text}>
                {formatMessage({ id: 'autoreport:metric_comment' })}
              </CardItemHeader>
            </CardItem>

            {slide.payload.positive.items.map((item, index) => (
              <CardItem key={item.kol.uuid}>
                <CardItemBorderTop data-pptx-object={PptxObjectType.Shape} />

                <span data-pptx-object={PptxObjectType.Text}>{index + 1}</span>
                <span>
                  <Image
                    priority
                    unoptimized
                    alt='kol thumbnail'
                    data-pptx-object={PptxObjectType.Image}
                    height={40}
                    src={getThumbnail(item.kol.uuid)}
                    style={{ borderRadius: '50%', marginRight: 16 }}
                    width={40}
                  />

                  <KolName data-pptx-object={PptxObjectType.Text}>
                    {item.kol.name}
                  </KolName>
                </span>

                <span data-pptx-object={PptxObjectType.Text}>{item.value}</span>
              </CardItem>
            ))}
          </Card>
        )}

        {slide.payload.negative && (
          <Card data-pptx-object={PptxObjectType.Shape}>
            <CardTitle data-pptx-object={PptxObjectType.Text} mb={5}>
              {slide.payload.negative.title}
            </CardTitle>

            <CardItem>
              <CardItemHeader data-pptx-object={PptxObjectType.Text}>
                {formatMessage({ id: 'autoreport:chart_field_order' })}
              </CardItemHeader>
              <CardItemHeader data-pptx-object={PptxObjectType.Text}>
                KOL
              </CardItemHeader>
              <CardItemHeader data-pptx-object={PptxObjectType.Text}>
                {formatMessage({ id: 'autoreport:metric_comment' })}
              </CardItemHeader>
            </CardItem>

            {slide.payload.negative.items.map((item, index) => (
              <CardItem key={item.kol.uuid}>
                <CardItemBorderTop data-pptx-object={PptxObjectType.Shape} />

                <span data-pptx-object={PptxObjectType.Text}>{index + 1}</span>
                <span>
                  <Image
                    priority
                    unoptimized
                    alt='kol thumbnail'
                    data-pptx-object={PptxObjectType.Image}
                    height={40}
                    src={getThumbnail(item.kol.uuid)}
                    style={{ borderRadius: '50%', marginRight: 16 }}
                    width={40}
                  />

                  <KolName data-pptx-object={PptxObjectType.Text}>
                    {item.kol.name}
                  </KolName>
                </span>

                <span data-pptx-object={PptxObjectType.Text}>{item.value}</span>
              </CardItem>
            ))}
          </Card>
        )}

        {slide.payload.neutral && (
          <Card data-pptx-object={PptxObjectType.Shape}>
            <CardTitle data-pptx-object={PptxObjectType.Text} mb={5}>
              {slide.payload.neutral.title}
            </CardTitle>

            <CardItem>
              <CardItemHeader data-pptx-object={PptxObjectType.Text}>
                {formatMessage({ id: 'autoreport:chart_field_order' })}
              </CardItemHeader>
              <CardItemHeader data-pptx-object={PptxObjectType.Text}>
                KOL
              </CardItemHeader>
              <CardItemHeader data-pptx-object={PptxObjectType.Text}>
                {formatMessage({ id: 'autoreport:metric_comment' })}
              </CardItemHeader>
            </CardItem>

            {slide.payload.neutral.items.map((item, index) => (
              <CardItem key={item.kol.uuid}>
                <CardItemBorderTop data-pptx-object={PptxObjectType.Shape} />

                <span data-pptx-object={PptxObjectType.Text}>{index + 1}</span>
                <span>
                  <Image
                    priority
                    unoptimized
                    alt='kol thumbnail'
                    data-pptx-object={PptxObjectType.Image}
                    height={40}
                    src={getThumbnail(item.kol.uuid)}
                    style={{ borderRadius: '50%', marginRight: 16 }}
                    width={40}
                  />

                  <KolName data-pptx-object={PptxObjectType.Text}>
                    {item.kol.name}
                  </KolName>
                </span>

                <span data-pptx-object={PptxObjectType.Text}>{item.value}</span>
              </CardItem>
            ))}
          </Card>
        )}

        {slide.payload.unknown && (
          <Card data-pptx-object={PptxObjectType.Shape}>
            <CardTitle data-pptx-object={PptxObjectType.Text} mb={5}>
              {slide.payload.unknown.title}
            </CardTitle>

            <CardItem>
              <CardItemHeader data-pptx-object={PptxObjectType.Text}>
                {formatMessage({ id: 'autoreport:chart_field_order' })}
              </CardItemHeader>
              <CardItemHeader data-pptx-object={PptxObjectType.Text}>
                KOL
              </CardItemHeader>
              <CardItemHeader data-pptx-object={PptxObjectType.Text}>
                {formatMessage({ id: 'autoreport:metric_comment' })}
              </CardItemHeader>
            </CardItem>

            {slide.payload.unknown.items.map((item, index) => (
              <CardItem key={item.kol.uuid}>
                <CardItemBorderTop data-pptx-object={PptxObjectType.Shape} />

                <span data-pptx-object={PptxObjectType.Text}>{index + 1}</span>
                <span>
                  <Image
                    priority
                    unoptimized
                    alt='kol thumbnail'
                    data-pptx-object={PptxObjectType.Image}
                    height={40}
                    src={getThumbnail(item.kol.uuid)}
                    style={{ borderRadius: '50%', marginRight: 16 }}
                    width={40}
                  />

                  <KolName data-pptx-object={PptxObjectType.Text}>
                    {item.kol.name}
                  </KolName>
                </span>

                <span data-pptx-object={PptxObjectType.Text}>{item.value}</span>
              </CardItem>
            ))}
          </Card>
        )}
      </ContentGrid>
    </SlideContainer>
  )
}

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 32px;

  width: 100%;
`

const CardItem = styled.div`
  display: grid;
  grid-template-columns: 64px 1fr 80px;
  align-items: center;
  gap: 8px;

  position: relative;
  padding: 12px 16px;
`

const CardItemBorderTop = styled.div`
  position: absolute;
  top: 0;

  width: 100%;
  height: 1px;
  background-color: ${({ theme }): string => theme.colors.border.divider};
`

const CardItemHeader = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }): string => theme.colors.text.secondary};
`

const KolName = styled.span`
  font-size: 12px;
  font-weight: 400;

  color: ${({ theme }): string => theme.colors.text.secondary};
`

export default SlideComponent
